/**
 * Runtime Analyzer
 * 
 * Analyzes runtime environment and provides capabilities, config, and manifest
 */

import { EnvironmentDetector, getEnvironmentDetector } from './EnvironmentDetector';
import type { 
  Environment, 
  RuntimeAnalysis, 
  RuntimeCapabilities, 
  RuntimeConfig,
  RuntimeManifest 
} from './types';

export class RuntimeAnalyzer {
  private detector: EnvironmentDetector;

  constructor(detector?: EnvironmentDetector) {
    this.detector = detector || getEnvironmentDetector();
  }

  analyze(): RuntimeAnalysis {
    const env = this.detector.getEnvironment();
    return {
      runtimeType: env.platform,
      capabilities: this.getCapabilities(),
      config: this.getRuntimeConfig(),
      environment: env
    };
  }

  getRuntimeType(): string {
    return this.detector.getEnvironment().platform;
  }

  getCapabilities(): RuntimeCapabilities {
    const env = this.detector.getEnvironment();
    return {
      isDevelopment: env.isDevelopment,
      isProduction: env.isProduction,
      nodeEnv: env.nodeEnv,
      hasVSCodeAPI: env.platform === 'vscode',
      hasBrowserAPI: env.platform === 'browser' || env.platform === 'standalone',
      hasNodeAPI: env.platform === 'node',
      architecture: env.arch,
      isEmbedded: env.platform === 'vscode' || env.platform === 'standalone'
    };
  }

  getRuntimeConfig(): RuntimeConfig {
    const env = this.detector.getEnvironment();
    return {
      env: {
        isDevelopment: env.isDevelopment,
        isProduction: env.isProduction,
        nodeEnv: env.nodeEnv
      },
      platform: env.platform,
      architecture: env.arch,
      version: '0.3.0',
      build: env.isDevelopment ? 'development' : 'production'
    };
  }

  getEnvironment(): Environment {
    return this.detector.getEnvironment();
  }

  createManifest(name: string = 'FreshCards'): RuntimeManifest {
    const analysis = this.analyze();
    const now = new Date();
    return {
      id: `manifest-${now.getTime()}`,
      name,
      version: analysis.config.version,
      build: analysis.config.build,
      runtime: analysis,
      metadata: {
        created: now,
        lastUpdated: now,
        platform: analysis.environment.platform,
        architecture: analysis.environment.arch
      }
    };
  }
}

// Singleton
let _analyzer: RuntimeAnalyzer | null = null;

export function getRuntimeAnalyzer(): RuntimeAnalyzer {
  if (!_analyzer) {
    _analyzer = new RuntimeAnalyzer();
  }
  return _analyzer;
}